import {
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import Layout from '../components/Layout';
import DoorStage from '../components/manufactured-doors/DoorStage';
import NoResultsRow from '../components/NoResultsRow';
import TableLoading from '../components/TableLoading';
import * as Routes from '../constants/Routes';
import useTable from '../hooks/useTable';
import { Door, useDoorsAdminQuery } from '../queries';

import theme from '../theme';
import dayjs from 'dayjs';
import DoorsAdminFilter, { DoorsAdminSearchFilter } from '../components/doors-admin/DoorsAdminFilter';
import { useDebounce } from 'use-debounce';

const useStyles = makeStyles({
  tableRow: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  activeStatus: {
    color: theme.palette.primary.main,
  },
  disabledStatus: {
    color: theme.palette.error.main,
  },
  buttonContainer: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
});

export default function DoorsAdmin() {
  const history = useHistory();
  const { page, pageSize, sortField, sortDirection, pageChanged, pageSizeChanged, sort } = useTable(
    'id',
    undefined,
    undefined,
    'desc'
  );
  const [filter, setFilter] = useState<DoorsAdminSearchFilter>({});

  const { loading: rawLoading, error, data, refetch } = useDoorsAdminQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      skip: pageSize * page,
      take: pageSize,
      sortDirection,
      sortField,
      textFilter: filter?.textFilter,
    },
  });

  const [loading] = useDebounce(rawLoading, 300); // this is to prevent calling the api with every key stroke in the text input.
  const classes = useStyles();

  useEffect(() => {
    pageChanged(0);
    if (refetch) {
      refetch();
    }
  }, [filter, refetch, pageChanged]);

  const onRowClick = (doorId: string) => {
    history.push(`${Routes.ViewDoor}/${doorId}`);
  };

  const getDoorStatus = (door: Partial<Door> | null | undefined) => {
    const status = door?.deletedAt ? 'Disabled' : 'Active';
    const className = door?.deletedAt ? classes.disabledStatus : classes.activeStatus;
    return (
      <Typography variant="body2" className={className}>
        {status}
      </Typography>
    );
  };

  const changePager = (page: number) => {
    pageChanged(page);
  };
  const changePagerSize = (e: any) => {
    pageSizeChanged(e);
  };

  return (
    <Layout>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Doors</Typography>
      </Box>
      <Box mt={3}>
        <TableContainer component={Paper}>
          <DoorsAdminFilter onFilterChanged={(filter) => setFilter(filter)} />
          <Divider />
          <Table aria-label="Manufactured doors table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortField === 'id'} direction={sortDirection} onClick={() => sort('id')}>
                    DDS Id
                  </TableSortLabel>
                </TableCell>
                <TableCell>Project Reference / Site</TableCell>
                <TableCell>Door Reference / Location</TableCell>
                <TableCell>Creation date</TableCell>
                <TableCell>Tag ID</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableLoading />}
              {!loading && !error && data?.doorsAdmin?.items.length === 0 && (
                <NoResultsRow message="No doors to display" />
              )}
              {!loading && error && <NoResultsRow message="There was an error fetching doors." />}
              {!loading &&
                !error &&
                data?.doorsAdmin &&
                data.doorsAdmin?.items?.length > 0 &&
                data.doorsAdmin.items.map((door, i) => (
                  <TableRow hover key={i} onClick={() => onRowClick(door.id)} className={classes.tableRow}>
                    <TableCell>{door.id}</TableCell>
                    <TableCell>{door.site?.name ?? door.projectReference}</TableCell>
                    <TableCell>{door.location ?? door.doorReference}</TableCell>
                    <TableCell>{dayjs(door.createdAt).format('D/MM/YYYY')}</TableCell>
                    <TableCell>{door.tagId}</TableCell>
                    <TableCell>
                      <DoorStage doorStageCode={door.status.code} />
                    </TableCell>
                    <TableCell>{getDoorStatus(door as Partial<Door>)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={data?.doorsAdmin?.total ?? 0}
            rowsPerPage={pageSize}
            page={page}
            onChangePage={(_, newPage) => changePager(newPage)}
            onChangeRowsPerPage={changePagerSize}
          />
        </TableContainer>
      </Box>
    </Layout>
  );
}
